/**
 * §6.7's re-plan confirmation (`304:12157`): saving a profile re-prices
 * every account that resolves to it, and a tenant sees the full impact --
 * every affected account's own before/after price, not a sample -- before
 * it happens, not after. Built on the shared `ConfirmDialog` (an
 * `alertdialog`, not a plain dialog): the pending spinner, the save-failure
 * surface and the disabled-while-pending buttons all come from there.
 *
 * `accounts` is `null` while the replan preview has not answered yet,
 * distinct from an empty array once it has and nothing is affected -- the
 * same absence-vs-zero shape the caller's own affected-count stat uses.
 * `previewFailure` is a second, distinct failure surface from `failure`:
 * `failure` is what the *save* did, `previewFailure` is what the *preview
 * fetch* did, and conflating them would show a save error while the
 * preview itself never loaded (or hide a load failure behind a dialog that
 * still invites a confirm).
 *
 * `DRAWN_WINDOW` accounts render as their own row, matching the frame; every
 * account beyond it still counts toward the title, the confirm label and
 * the total delta -- only its own row folds into one overflow line, its sum
 * built from the same real per-account figures the table rows use, not an
 * approximation (`owner-decisions-align-304-12157-02`: every affected
 * account, not a top-N sample).
 */

import { AlertCircle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { defineCatalogue, getLocale, type Locale } from '../../../../i18n';
import { credits } from '../../../../i18n/format';
import type { ApiFailure } from '../../../../lib/http/errors';
import { ConfirmDialog } from '../../../../chrome/form';
import { Absent } from '../../../../chrome/measure';
import { EmptyState } from '../../../../chrome/state';
import { DataTable } from '../../../../chrome/table';

const replanCatalogue = defineCatalogue(
  {
    'title.counted.one': 'Aplicar a {count} conta',
    'title.counted.other': 'Aplicar a {count} contas',
    'title.pending': 'Aplicar alteração',
    description: 'Esta alteração replaneja as contas que usam este perfil.',
    'confirm.counted.one': 'Aplicar a {count} conta',
    'confirm.counted.other': 'Aplicar às {count} contas',
    'confirm.pending': 'Aplicar',
    'column.account': 'Conta',
    'column.current': 'Hoje',
    'column.projected': 'Depois',
    'column.delta': 'Diferença',
    'delta.unchanged': 'sem mudança',
    'overflow.one': 'e mais {count} conta, somando {delta} por dia',
    'overflow.other': 'e mais {count} contas, somando {delta} por dia',
    total: '{delta} créditos por dia no total',
    // Ruling `owner-decisions-align-136-950-03`: the projected `funded_until`
    // beside the current one, not just the projected figure alone.
    runway: 'A coleta chega a {projected} em vez de {current}',
    'empty.title': 'Nenhuma conta afetada',
    'unused.title': 'Aplicar alterações',
    'unused.description':
      'Nenhuma conta usa este perfil agora. As alterações valem para as contas que passarem a usá-lo.',
    'unused.confirm': 'Aplicar alterações',
    handleNone: 'sem identificador',
    // Its own sr-only table caption, distinct from the dialog's visible
    // title: the two are announced separately by a screen reader, and a
    // caption repeating the title verbatim also duplicates its text node
    // for a sighted query.
    'table.caption': 'Contas afetadas',
    // Never `failureSentence`/`ErrorState` for this one: `invalid` returns the
    // api's own English text verbatim (`lib/http/errors.ts`'s `toInvalid`).
    previewFailed: 'Não foi possível calcular o impacto agora. Tente de novo.',
  },
  {
    'title.counted.one': 'Apply to {count} account',
    'title.counted.other': 'Apply to {count} accounts',
    'title.pending': 'Apply the change',
    description: 'This change re-plans the accounts that use this profile.',
    'confirm.counted.one': 'Apply to {count} account',
    'confirm.counted.other': 'Apply to {count} accounts',
    'confirm.pending': 'Apply',
    'column.account': 'Account',
    'column.current': 'Today',
    'column.projected': 'After',
    'column.delta': 'Difference',
    'delta.unchanged': 'no change',
    'overflow.one': 'and {count} more account, totalling {delta}/day',
    'overflow.other': 'and {count} more accounts, totalling {delta}/day',
    total: '{delta} credits/day in total',
    runway: 'Collection reaches {projected} instead of {current}',
    'empty.title': 'No affected accounts',
    'unused.title': 'Apply changes',
    'unused.description':
      'No account uses this profile yet. The changes apply to the accounts that start using it.',
    'unused.confirm': 'Apply changes',
    handleNone: 'no handle',
    'table.caption': 'Affected accounts',
    previewFailed: 'Could not calculate the impact right now. Try again.',
  },
);
const rt = replanCatalogue.t;
const rtPlural = replanCatalogue.plural;

export interface ReplanAccountRow {
  id: string;
  handle: string | null;
  current: number;
  projected: number;
}

// Matches the frame (`304:12157`): four rows draw on their own, the rest
// fold into one summed overflow line.
const DRAWN_WINDOW = 4;

/** A signed figure ("+500", "-120"), never an absolute value. Deliberately
 *  local: `credits()` omits a sign on a positive figure everywhere else it is
 *  used, and this dialog is the one place a tenant reads both directions side
 *  by side. */
export function signedCredits(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { signDisplay: 'exceptZero' }).format(value);
}

function shortDate(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(
    new Date(iso),
  );
}

/** Both ends of the runway as short dates, or `null` when either is unknown:
 *  half a comparison would read as a claim about the other half. */
export function runwayDates(
  projectedFundedUntil: string | null,
  currentFundedUntil: string | null,
  locale: Locale,
): { projected: string; current: string } | null {
  if (projectedFundedUntil === null || currentFundedUntil === null) return null;
  return {
    projected: shortDate(projectedFundedUntil, locale),
    current: shortDate(currentFundedUntil, locale),
  };
}

function deltaCell(row: ReplanAccountRow, locale: Locale): ReactNode {
  const delta = row.projected - row.current;
  return delta === 0 ? rt('delta.unchanged') : signedCredits(delta, locale);
}

const COLUMNS: ColumnDef<ReplanAccountRow, unknown>[] = [
  {
    accessorKey: 'handle',
    header: rt('column.account'),
    cell: (info) =>
      info.getValue<ReplanAccountRow['handle']>() ?? <Absent reason={{ text: rt('handleNone') }} />,
  },
  {
    id: 'current',
    accessorFn: (row) => row.current,
    header: rt('column.current'),
    meta: { numeric: true },
    cell: (info) => credits(info.getValue<number>(), getLocale()),
  },
  {
    id: 'projected',
    accessorFn: (row) => row.projected,
    header: rt('column.projected'),
    meta: { numeric: true },
    cell: (info) => credits(info.getValue<number>(), getLocale()),
  },
  {
    id: 'delta',
    accessorFn: (row) => row,
    header: rt('column.delta'),
    meta: { numeric: true },
    cell: (info) => deltaCell(info.getValue<ReplanAccountRow>(), getLocale()),
  },
];

export function ReplanConfirmation(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every affected account, `null` while the preview has not answered. */
  accounts: ReplanAccountRow[] | null;
  /** The total change across every affected account -- `null` alongside `accounts`. */
  deltaCreditsPerDay: number | null;
  /** The tenant's real, currently funded window -- independent of this edit. */
  currentFundedUntil: string | null;
  /** What the funded window becomes if this edit is applied. */
  projectedFundedUntil: string | null;
  previewFailure: ApiFailure | null;
  pending: boolean;
  failure: ApiFailure | null;
  onConfirm: () => void | Promise<unknown>;
}): ReactNode {
  const locale = getLocale();
  const count = props.accounts?.length ?? null;
  const windowRows = props.accounts?.slice(0, DRAWN_WINDOW) ?? [];
  const overflowRows = props.accounts?.slice(DRAWN_WINDOW) ?? [];

  const title =
    count === null ? rt('title.pending') : rtPlural('title.counted.other', count, { count });
  const confirmLabel =
    count === null ? rt('confirm.pending') : rtPlural('confirm.counted.other', count, { count });

  // Nothing to re-plan, so nothing to weigh: a plain confirm instead of an empty table.
  if (count === 0 && props.previewFailure === null) {
    return (
      <ConfirmDialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title={rt('unused.title')}
        description={rt('unused.description')}
        confirmLabel={rt('unused.confirm')}
        pending={props.pending}
        failure={props.failure}
        onConfirm={props.onConfirm}
      />
    );
  }

  const dates = runwayDates(props.projectedFundedUntil, props.currentFundedUntil, locale);
  const runway = dates === null ? null : rt('runway', dates);

  return (
    <ConfirmDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={title}
      description={rt('description')}
      confirmLabel={confirmLabel}
      pending={props.pending}
      failure={props.failure}
      confirmDisabled={props.previewFailure !== null || count === null}
      onConfirm={props.onConfirm}
    >
      {props.previewFailure !== null ? (
        <p role="alert" className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0" />
          {rt('previewFailed')}
        </p>
      ) : (
        <>
          <DataTable<ReplanAccountRow>
            columns={COLUMNS}
            data={windowRows}
            loading={props.accounts === null}
            getRowId={(row) => row.id}
            caption={rt('table.caption')}
            emptyState={<EmptyState title={rt('empty.title')} />}
          />
          {overflowRows.length > 0 && (
            <p data-testid="replan-overflow" className="text-caption text-muted-foreground">
              {rtPlural('overflow.other', overflowRows.length, {
                count: overflowRows.length,
                delta: signedCredits(
                  overflowRows.reduce((sum, row) => sum + (row.projected - row.current), 0),
                  locale,
                ),
              })}
            </p>
          )}
          {props.deltaCreditsPerDay !== null && count !== 0 && (
            <div className="flex flex-col gap-1">
              <p className="font-medium" data-testid="replan-total-delta">
                {rt('total', { delta: signedCredits(props.deltaCreditsPerDay, locale) })}
              </p>
              {runway !== null && (
                <p className="text-caption text-muted-foreground" data-testid="replan-runway">
                  {runway}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </ConfirmDialog>
  );
}
